import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import MenuIcon from '@mui/icons-material/Menu'
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme as useMuiTheme
} from '@mui/material'
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router-dom'
import { Images, LINKS } from 'utils'

import { useThemeCustom } from '../../theme/ThemeContext'
import LanguageSelector from '../LanguageSelector'
import SmartImage from '../SmartImage'

const pages = ['home', 'service', 'resume', 'about', 'contact', 'tools']

interface IProps {
  children?: React.ReactNode
}

const Header: React.FC<IProps> = (props) => {
  const [t] = useTranslation()
  const { isDarkMode, toggleTheme, theme } = useThemeCustom()
  const muiTheme = useMuiTheme()
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null)

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }

  return (
    <>
      <AppBar
        style={{
          backgroundColor: theme.header.bg
        }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: { xs: 0.5, md: 1 } }}>
            {/* Desktop logo */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 2 }}>
              <Link to={LINKS.home}>
                <SmartImage
                  src={
                    isDarkMode ? Images.home.logoWhite : Images.home.logoColor
                  }
                  alt="Logo"
                  placeholder="NCM"
                  width={50}
                  height={50}
                  customStyle={{ height: 50, width: 50, objectFit: 'contain' }}
                />
              </Link>
            </Box>

            {/* Mobile menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="open navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit">
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' }
                }}>
                {pages.map((page) => (
                  <MenuItem key={page} onClick={handleCloseNavMenu}>
                    <Typography textAlign="center">
                      <Link
                        to={`/${page}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}>
                        {t(`nav.${page}`)}
                      </Link>
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Mobile centered logo */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center'
              }}>
              <Link to={LINKS.home}>
                <SmartImage
                  src={
                    isDarkMode ? Images.home.logoWhite : Images.home.logoColor
                  }
                  alt="Logo"
                  placeholder="NCM"
                  width={40}
                  height={40}
                  customStyle={{ height: 40, width: 40, objectFit: 'contain' }}
                />
              </Link>
            </Box>

            {/* Desktop nav */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  width: '100%',
                  maxWidth: 720
                }}>
                {pages.map((page) => (
                  <Button
                    key={page}
                    component={NavLink}
                    to={`/${page}`}
                    onClick={handleCloseNavMenu}
                    sx={{
                      my: 0.5,
                      mx: 0.25,
                      minHeight: 48,
                      minWidth: 48,
                      px: { md: 1.5, lg: 2 },
                      color: muiTheme.palette.text.primary,
                      fontSize: { md: '0.875rem', lg: '1rem' },
                      '&.active': {
                        fontWeight: 700
                      }
                    }}>
                    {t(`nav.${page}`)}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{ mr: 0.5 }}
                aria-label={
                  isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
                }>
                {isDarkMode ? (
                  <DarkModeIcon fontSize="small" />
                ) : (
                  <LightModeIcon fontSize="small" />
                )}
              </IconButton>
              <LanguageSelector />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Box>{props.children}</Box>
    </>
  )
}

export default memo(Header)
